import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  Input,
} from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { GasInputBoolean } from '@cikrf/gas-utils/decorators';

import { GasProgressColor } from '../../common/gas-progress/gas-progress.interface';
import { GasIcon } from '../../utils/gas-icons/icon/gas-icon.namespace';

/** @see https://www.figma.com/file/YDxRp3ZZaGFI4UoB6mCcBr/GAS20_Administration_Vote?node-id=246%3A18411 */

/**
 * @example <gas-progress-container title="text-title-string" subtitle="subtitle"> text-content-string </gas-progress-container>
 */
@Component({
  selector: 'gas-progress-container',
  templateUrl: './gas-progress-container.component.html',
  styleUrls: ['./gas-progress-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GasProgressContainerComponent {
  @Input()
  public title: string;

  @Input()
  public subtitle: string;

  @Input()
  public percent = 0;

  @Input()
  public step = 1;

  @HostBinding('class._border')
  @GasInputBoolean()
  @Input()
  public border: string | boolean = false;

  @HostBinding('class._block-shadow')
  @GasInputBoolean()
  @Input()
  public boxShadow: string | boolean = false;

  @GasInputBoolean()
  @Input()
  public warning: string | boolean = false;

  @GasInputBoolean()
  @Input()
  public error: string | boolean = false;

  @HostBinding('class._inline-content')
  @GasInputBoolean()
  @Input()
  public inlineContent: string | boolean = false;

  public isExpanded$: Observable<boolean>;

  private isExpandedSubject$ = new BehaviorSubject<boolean>(false);

  public get progressColor(): GasProgressColor {
    if (this.warning) {
      return 'warning';
    }

    if (this.error) {
      return 'error';
    }

    if (this.percent === 100) {
      return 'success';
    }

    return 'brand';
  }

  public get progressIcon(): GasIcon.Icons {
    if (this.warning) {
      return  GasIcon.Notifications.LightWarning;
    }

    if (this.error) {
      return  GasIcon.Notifications.LightError;
    }

    if (this.percent === 100) {
      return  GasIcon.Notifications.LightSuccess;
    }

    return null;
  }

  constructor() {
    this.isExpanded$ = this.isExpandedSubject$.asObservable();
  }

  public toggleExpanded(element: HTMLElement): void {
    if (!this.hasContent(element)) {
      return;
    }

    this.isExpandedSubject$.next(!this.isExpandedSubject$.getValue());
  }

  public hasContent(element: HTMLElement): boolean {
    return Boolean(element?.hasChildNodes());
  }
}
